import {
  addIntegration,
  browserTracingIntegration,
  captureException,
  init,
  lazyLoadIntegration,
} from '@sentry/react'

/**
 * Sentry client init. This app is a static SPA (Firebase Hosting, no server
 * runtime), so only browser-side capture applies — the SDK's default global
 * `onerror` + `unhandledrejection` handlers catch the "won't load" / chunk-load
 * class of failure that previously surfaced nowhere.
 *
 * Runs as an import side-effect (imported first in router.tsx). Guarded so it
 * only activates when a DSN is configured (via `.env.production`) and only in a
 * real browser — never during the build-time prerender that generates the shell.
 */
const dsn = import.meta.env.VITE_SENTRY_DSN

if (dsn && typeof window !== 'undefined') {
  init({
    dsn,
    environment: import.meta.env.MODE,
    // Set by CI to the deploy git SHA so issues point at the release that
    // introduced them. Undefined locally — Sentry falls back to no release.
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    integrations: [
      browserTracingIntegration(),
    ],
    // Low trace sampling keeps us within the free tier; errors are always sent.
    tracesSampleRate: 0.1,
    // Don't record normal sessions — only capture a replay when something errors
    // (the "show me the broken screen" case), which stays within quota.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  })

  const loadReplay = () => {
    void lazyLoadIntegration('replayIntegration')
      .then((createReplay) => {
        addIntegration(createReplay())
      })
      .catch(() => {
        // Replay is optional telemetry; a CDN or extension failure must never
        // interfere with application startup.
      })
  }

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(loadReplay, { timeout: 5000 })
  } else {
    globalThis.setTimeout(loadReplay, 2000)
  }
}

export const Sentry = { captureException }
